import type { Tracklist } from "@api/tracklists/types";
import { AltText, Card, Column, LineClamp, Row } from "@components/Layout";
import useProgramme from "@components/Programme/hooks/useProgramme";
import ProgrammeDataBadge from "@components/ProgrammeDataBadge";
import { Template } from "@components/Template";
import UserIcon from "@components/UserIcon";
import { Button, Image } from "@kobalte/core";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import usePlay from "@layouts/App/context/Player/hooks/usePlay";
import { FaRegularBookmark, FaRegularComments, FaSolidBookmark, FaSolidComments } from "solid-icons/fa";
import { RiSystemThumbUpFill, RiSystemThumbUpLine } from "solid-icons/ri";
import { Show, type VoidComponent } from "solid-js";
import { Portal } from "solid-js/web";
import { ProgrammeImage } from "./styled";

const ProgrammeCard: VoidComponent<{ tracklist: Tracklist; onLike: () => void; onBookmark: () => void }> = (props) => {
	const { playTracklist } = usePlay();
	const { updateBookmark, updateLike } = useProgramme();

	const handleLike = () => updateLike(props.tracklist).then(props.onLike);
	const handleBookmark = () => updateBookmark(props.tracklist).then(props.onBookmark);

	return (
		<Portal mount={document.getElementById(props.tracklist.uuid) ?? undefined}>
			<Card padding="10px">
				<Row gap="10px">
					<ProgrammeImage>
						<Image.Img src={props.tracklist.photo} />
						<Image.Fallback>
							<Template />
						</Image.Fallback>
					</ProgrammeImage>
					<Column gap="10px">
						<h3 class={LineClamp}>{props.tracklist.name}</h3>
						<Row gap="10px" alignItems="center">
							<Row w="max-content" maxWidth="calc(100% - 50px)" gap="10px" alignItems="center">
								<UserIcon diameter="30px" src={props.tracklist.author.photo} />
								<AltText class={LineClamp}>{props.tracklist.author.nickname}</AltText>
							</Row>
							<PlayButton
								tracklistUuid={props.tracklist.uuid}
								onClick={() => playTracklist(props.tracklist, 0, false)}
								diameter="40px"
							/>
						</Row>
						<ProgrammeDataBadge tracklist={props.tracklist} fontSize="10px" />
						<Row gap="10px">
							<Button.Root onclick={handleLike}>
								<AltText>
									<Row gap="5px">
										<Show when={props.tracklist.liked} fallback={<RiSystemThumbUpLine />}>
											<RiSystemThumbUpFill />
										</Show>
										{props.tracklist.rating}
									</Row>
								</AltText>
							</Button.Root>
							<Button.Root onclick={handleBookmark}>
								<AltText>
									<Row gap="5px">
										<Show when={props.tracklist.bookmarked} fallback={<FaRegularBookmark />}>
											<FaSolidBookmark />
										</Show>
										{props.tracklist.bookmark_count}
									</Row>
								</AltText>
							</Button.Root>
							<AltText>
								<Row gap="5px">
									<Show when={props.tracklist.commented} fallback={<FaRegularComments />}>
										<FaSolidComments />
									</Show>
									{props.tracklist.comments}
								</Row>
							</AltText>
						</Row>
					</Column>
				</Row>
			</Card>
		</Portal>
	);
};
export default ProgrammeCard;
